import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Card, Form, Row, Col, Button } from '@themesberg/react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { showErrorToast, showSuccessToast } from "../utils/toast";

import { API_URL, END_POINT_COMPANY } from '../const';
import { getCompanySettings, saveCompanySettings } from '../utils/companySettings';

const CompanySettingsPage = () => {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(Boolean(id));

  const rawToken = sessionStorage.getItem('quotetoken');
  const token = rawToken?.replace(/^"|"$/g, '');

  useEffect(() => {
    const fetchCompany = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}${END_POINT_COMPANY}/${id}`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status) {
          setCompany(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching company for settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id, token]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      company_id: id || company?.id || '',
      company_name: company?.name || '',
      company_address: company?.address || '',
      company_city: '',
      company_phone: '',
      company_email: '',
      company_gst_no: '',
      company_currency: 'INR',
      company_tax_percentage: '18',
      company_note: '',
      ...getCompanySettings(id || company?.id || ''),
    },
    validationSchema: Yup.object({
      company_name: Yup.string().required('Company name is required'),
      company_address: Yup.string().required('Company address is required'),
      company_email: Yup.string().email('Enter a valid email').nullable(),
      company_phone: Yup.string().nullable(),
      company_gst_no: Yup.string().nullable(),
      company_currency: Yup.string().required('Currency is required'),
      company_tax_percentage: Yup.string().nullable(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const targetCompanyId = id || values.company_id || company?.id || '';
        saveCompanySettings(targetCompanyId, values);
        showSuccessToast('Company settings have been updated successfully.')
      } catch (error) {
        showErrorToast('Unable to save company settings.')
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!company) return;

    const savedSettings = getCompanySettings(company.id);
    formik.setValues({
      ...savedSettings,
      company_id: company.id,
      company_name: company.name || savedSettings.company_name || '',
      company_address: company.address || savedSettings.company_address || '',
    });
  }, [company]);

  return (
    <div className="mx-auto py-4" style={{ maxWidth: '1100px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="page-breadcrumb mb-0">
            {company ? `${company.name} Settings` : 'Company Settings'}
          </h2>
        </div>
        <Link to="/company">
          <Button variant="secondary">Back to Company</Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <div className="mt-2">Loading company settings...</div>
        </div>
      ) : (
        <Card border="light" className="shadow-sm">
          <Card.Body>
            <Form onSubmit={formik.handleSubmit}>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="company_name">
                    <Form.Label>Company Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...formik.getFieldProps('company_name')}
                      isInvalid={formik.touched.company_name && !!formik.errors.company_name}
                    />
                    <Form.Control.Feedback type="invalid">{formik.errors.company_name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group controlId="company_currency">
                    <Form.Label>Currency <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="company_currency"
                      value={formik.values.company_currency || 'INR'}
                      onChange={formik.handleChange}
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Group controlId="company_address">
                    <Form.Label>Company Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...formik.getFieldProps('company_address')}
                      isInvalid={formik.touched.company_address && !!formik.errors.company_address}
                    />
                    <Form.Control.Feedback type="invalid">{formik.errors.company_address}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group controlId="company_city">
                    <Form.Label>City</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps('company_city')} />
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group controlId="company_phone">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps('company_phone')} />
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group controlId="company_email">
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" {...formik.getFieldProps('company_email')} />
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group controlId="company_gst_no">
                    <Form.Label>GST No.</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps('company_gst_no')} />
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group controlId="company_tax_percentage">
                    <Form.Label>Tax %</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps('company_tax_percentage')} />
                  </Form.Group>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Group controlId="company_note">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control as="textarea" rows={3} {...formik.getFieldProps('company_note')} />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Link to="/company">
                  <Button variant="outline-secondary">Cancel</Button>
                </Link>
                <Button type="submit" variant="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default CompanySettingsPage;
